import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import { liteClient as algoliasearch } from 'algoliasearch/lite';

const searchClient = algoliasearch(process.env.ALGOLIA_APP_ID, process.env.ALGOLIA_API_KEY);

/**
 * Gets URL parameters for search functionality.
 *
 * @returns {{searchTool: string, searchQuery: string, coupon: string, tz: string}} object containing search parameters
 */
function getUrlParams() {
  const urlParams = new URLSearchParams(window.location.search);

  return {
    searchTool: urlParams.get('searchTool') || '',
    searchQuery: urlParams.get('searchQuery') || '',
    coupon: urlParams.get('coupon') || '',
    tz: urlParams.get('tz') || '',
  };
}

/**
 * Builds search URL with query and preserves coupon, tz, and searchTool parameters.
 *
 * @param {string} query the search query
 *
 * @returns {string} complete search URL with preserved parameters
 */
function buildSearchUrl(query) {
  const { coupon, tz } = getUrlParams();
  const params = new URLSearchParams();

  params.set('searchQuery', query);

  if (coupon) {
    params.set('coupon', coupon);
  }

  if (tz) {
    params.set('tz', tz);
  }

  return `/search?${params.toString()}`;
}

/**
 * Creates autocomplete sources for query suggestions and tests.
 *
 * @param {string} query the search query
 *
 * @returns {Array} array of autocomplete sources
 */
function createAutocompleteSources(query) {
  return [
    {
      sourceId: 'tests',
      getItems() {
        return getAlgoliaResults({
          searchClient,
          queries: [
            {
              indexName: process.env.ALGOLIA_INDEX_NAME,
              query,
            },
          ],
        });
      },
      templates: {
        item({ item, components, html }) {
          return html`
            <a href="${buildSearchUrl(item.name)}">${components.Highlight({ hit: item, attribute: 'name' })}</a>
          `;
        },
      },
    },
  ];
}

/**
 * Initializes autocomplete widget with the given container element and button.
 *
 * @param {HTMLElement} container the autocomplete container element
 * @param {HTMLElement|null} button the submit button element
 */
function initializeAutocompleteWidget(container, button) {
  let currentQuery = '';
  button.onclick = () => {
    location.href = buildSearchUrl(currentQuery);

    return false;
  };

  const autocompleteConfig = {
    container,
    panelContainer: container,
    placeholder: 'Enter Term or Test Code',
    openOnFocus: true,
    detachedMediaQuery: 'none',
    classNames: {
      form: 'autocomplete-form',
      panel: 'autocomplete-panel',
    },
    onStateChange({ state }) {
      currentQuery = state.query || '';
    },
    getSources({ query }) {
      return createAutocompleteSources(query);
    },
    onSubmit({ state }) {
      location.href = buildSearchUrl(state.query);
    },
  };

  return autocomplete(autocompleteConfig);
}

/**
 * Adds fallback click handler to a search button to capture input value.
 *
 * @param {HTMLElement} input the search input element
 * @param {HTMLElement} button the submit button element
 */
function addSearchButtonFallback(input, button) {
  button.addEventListener('click', (e) => {
    const query = input.value?.trim();

    if (query && button.onclick && !button.onclick.toString().includes('searchQuery')) {
      e.preventDefault();
      location.href = buildSearchUrl(query);
    }
  });
}

/**
 * Initialize all autocomplete widgets by discovering elements with data-autocomplete="algolia".
 */
function initializeAutocomplete() {
  const containers = document.querySelectorAll('[data-autocomplete="algolia"]');
  const instances = [];

  containers.forEach((container) => {
    const button = container.parentElement.parentElement.querySelector('button[type="submit"]');
    const input = container.querySelector('input[type="search"]');
    const instance = initializeAutocompleteWidget(container, button);
    instances.push({ instance, container });

    if (button && input) {
      addSearchButtonFallback(input, button);
    }
  });

  document.addEventListener('click', (e) => {
    instances.forEach(({ instance, container }) => {
      if (!container.contains(e.target)) {
        instance.setIsOpen(false);
      }
    });
  });
}

initializeAutocomplete();
